import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import Fade from '@material-ui/core/Fade';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: '480px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

/**
 * Modal de edición manual de la fecha de ingreso sugerida de un vehículo incluido (VEC-3335). No
 * bloquea fechas fuera del período operativo del programa — solo advierte y deja confirmar igual
 * (la validación real la hace el backend, esto es una ayuda visual antes de mandar la request).
 *
 * Props:
 *   open             {boolean}
 *   vehiculo         {object}   - fila de la grilla (id, movil_dominio, movil_unidad, region, delta_km, fecha_sugerida)
 *   periodoOperativo {object}   - { fecha_inicio, fecha_fin } del programa, en formato YYYY-MM-DD
 *   onClose          {function}
 *   onConfirmar      {function(nuevaFecha)} - devuelve una Promise; el modal cierra solo si resuelve.
 */
const ProgramasPreventivosEditarFechaModal = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const [nuevaFecha, setNuevaFecha] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (props.open && props.vehiculo) {
            setNuevaFecha(props.vehiculo.fecha_sugerida || '');
        }
    }, [props.open, props.vehiculo]);

    if (!props.vehiculo) return null;

    var periodo = props.periodoOperativo || {};
    var fueraDePeriodo = !!nuevaFecha
        && ((periodo.fecha_inicio && nuevaFecha < periodo.fecha_inicio) || (periodo.fecha_fin && nuevaFecha > periodo.fecha_fin));
    var puedeConfirmar = nuevaFecha !== '';

    var handleConfirmar = function() {
        if (!puedeConfirmar || loading) return;
        setLoading(true);
        props.onConfirmar(nuevaFecha)
            .then(function() {
                setLoading(false);
                props.onClose();
            })
            .catch(function() {
                setLoading(false);
            });
    };

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            className={classes.modal}
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={props.open}>
                <Box sx={modalStyle}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">
                            <i className="fa fa-calendar"></i>{' '}
                            <FormattedMessage id="programasPreventivosEditarFecha.titulo" defaultMessage="Editar fecha sugerida" />
                        </h4>
                        <button type="button" className="close" onClick={props.onClose} aria-label="close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="p-2 mb-3" style={{ background: '#f8f9fa', borderRadius: '4px' }}>
                        <strong>
                            {props.vehiculo.movil_dominio}
                            {props.vehiculo.movil_unidad ? ' · ' + props.vehiculo.movil_unidad : ''}
                        </strong>
                        <div className="text-muted" style={{ fontSize: '13px' }}>
                            {[
                                props.vehiculo.region,
                                props.vehiculo.delta_km !== null && props.vehiculo.delta_km !== undefined
                                    ? intl.formatMessage(
                                        { id: 'programasPreventivosExclusion.resumen_delta', defaultMessage: 'Delta: {delta} km' },
                                        { delta: props.vehiculo.delta_km.toLocaleString('es-AR') }
                                    )
                                    : null,
                            ].filter(Boolean).join(' · ')}
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6 form-group">
                            <label>
                                <FormattedMessage id="programasPreventivosEditarFecha.label_fecha_actual" defaultMessage="Fecha sugerida actual" />
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={props.vehiculo.fecha_sugerida || ''}
                                disabled
                            />
                        </div>
                        <div className="col-6 form-group">
                            <label>
                                <FormattedMessage id="programasPreventivosEditarFecha.label_fecha_nueva" defaultMessage="Nueva fecha de ingreso" /> *
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={nuevaFecha}
                                onChange={function(e) { setNuevaFecha(e.target.value); }}
                            />
                        </div>
                    </div>

                    {fueraDePeriodo && (
                        <div className="alert alert-warning" style={{ fontSize: '13px' }}>
                            <i className="fa fa-exclamation-triangle"></i>{' '}
                            <FormattedMessage
                                id="programasPreventivosEditarFecha.advertencia_fuera_de_periodo"
                                defaultMessage="La fecha ingresada está fuera del período operativo del programa. El cambio queda registrado en el historial."
                            />
                        </div>
                    )}

                    <div className="text-right mt-3">
                        <button type="button" className="btn btn-secondary mr-1" onClick={props.onClose} disabled={loading}>
                            <FormattedMessage id="programasPreventivosExclusion.btn_cancelar" defaultMessage="Cancelar" />
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleConfirmar} disabled={!puedeConfirmar || loading}>
                            <i className="fa fa-check-circle"></i>{' '}
                            <FormattedMessage id="programasPreventivosEditarFecha.btn_confirmar" defaultMessage="Guardar fecha" />
                        </button>
                    </div>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ProgramasPreventivosEditarFechaModal;
